/**
 * Created by jonas on 2021-02-02.
 */
import {GameModel} from "@nolimitcity/slot-game/bin/model/GameModel";
import {
    DjPsychoWinTypes,
    IBigWinLevel,
    IDjPsychoGameData,
    IDjPsychoInitData,
    IFeaturePosition,
    INeighboursData,
    IWinClusterData,
    IWinRatioConfig,
    IWinRatios
} from "./IDjPsychoData";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {StateMatrix} from "../utils/StateMatrix";
import {DjPsychoSymbolStates} from "../reels/DjPsychoSymbol";
import {DjPsycho} from "../DjPsycho";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import {DjPsychoGameMode} from "./DjPsychoGameMode";

export class DjPsychoModel extends GameModel<IDjPsychoInitData, IDjPsychoGameData> {
    public winMatrix: StateMatrix<DjPsychoSymbolStates>;
    public isRestore: boolean = false;
    public xKnobData: IFeaturePosition;
    public xNData: INeighboursData[] = [];
    public xZData: INeighboursData[] = [];
    public xMData: INeighboursData[] = [];
    public xBPMPositions: number[][] = [];
    public winRatioConfig: IWinRatioConfig;
    public noWinCount: number = 0;
    public winRatio: number;
    public fsLeftCount: number = -1;
    private _infectPos: number[] = [];
    private _xMPos: number[] = [];
    private WIN_RATIOS: IWinRatios = {
        normalWin: [
            {
                ratio: 0,
                duration: 0,
                type: DjPsychoWinTypes.TINY
            },
            {
                ratio: 1.5,
                duration: 0,
                type: DjPsychoWinTypes.SMALL
            },
            {
                ratio: 3,
                duration: 1,
                type: DjPsychoWinTypes.MEDIUM
            },
            {
                ratio: 5,
                duration: 2,
                type: DjPsychoWinTypes.LARGE
            }
        ],
        bigWin: [
            {
                from: 0,
                to: 25,
                id: 1,
                ratio: 15,
                duration: 4.048,
                type: DjPsychoWinTypes.BIG_WIN_01
            },
            {
                from: 25,
                to: 50,
                id: 2,
                ratio: 25,
                duration: 3.810,
                type: DjPsychoWinTypes.BIG_WIN_02
            },
            {
                from: 50,
                to: 100,
                id: 3,
                ratio: 50,
                duration: 3.810,
                type: DjPsychoWinTypes.BIG_WIN_03
            },
            {
                from: 100,
                to: 500,
                id: 4,
                ratio: 100,
                duration: 7.321,
                type: DjPsychoWinTypes.BIG_WIN_04
            },
            {
                from: 500,
                to: Number.MAX_VALUE,
                id: 5,
                ratio: 500,
                duration: 7.232,
                type: DjPsychoWinTypes.BIG_WIN_05
            }
        ]
    };

    constructor(api: ApiPlugin, triggerEvents: boolean = true) {
        super(api, triggerEvents);
        api.events.once("featureName", (data: string) => this.onFeatureName(data));
    }

    public getBigWinLevels(ratio: number): IBigWinLevel[] {
        let levels: IBigWinLevel[] = [];
        const length: number = this.WIN_RATIOS.bigWin.length;
        for (let index: number = 0; index < length; index++) {
            if (ratio >= this.WIN_RATIOS.bigWin[index].ratio) {
                levels.push(this.WIN_RATIOS.bigWin[index]);
            }
        }

        if (levels.length > 0) {
            levels = this.setLastConfig(ratio, levels);
        }
        return levels;
    }

    public avalancheMode(mode: DjPsychoGameMode): boolean {
        return mode === DjPsychoGameMode.NORMAL_AVALANCHE || mode === DjPsychoGameMode.FREESPIN_AVALANCHE || mode === DjPsychoGameMode.FREESPIN_SUPER_AVALANCHE;
    }

    public endOfAvalanche(mode: DjPsychoGameMode, nextMode: DjPsychoGameMode): boolean {
        return this.avalancheMode(mode) && !this.avalancheMode(nextMode);
    }

    // #124
    public onBet(betData: APIFeatureBetEventData): void {
        for (let index: number = 0; index < DjPsycho.model.initData.featureBuyTimesBetValue.length; index++) {
            if (DjPsycho.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                DjPsycho.buyFeatureName = betData.featureName;
                break;
            }
        }
    }

    protected onInit(data: IDjPsychoInitData): void {
        super.onInit(data);
        this.isRestore = data.isRestoreState;
        const secondReels = data.secondReels;
        const reels = data.reels;
        if (DjPsycho.model.isRestore) {
            secondReels.forEach((secondReels: string[], reelId: number) => {
                secondReels.forEach((name: string, symId: number) => {
                    if (name !== "X") {
                        this._infectPos.push((DjPsycho.reelsNum * (reelId)) + symId);
                    }
                })
            });
            reels.forEach((reels: string[], reelId: number) => {
                reels.forEach((name: string, symId: number) => {
                    if (name === "xM" && secondReels[reelId][symId] !== "W") {
                        this._xMPos.push((DjPsycho.reelsNum * (reelId)) + symId);
                    }
                })
            });
        }
    }

    public onGame(data: IDjPsychoGameData): void {
        super.onGame(data);
        this.isRestore = false;
        if (DjPsycho.model.data.mode.indexOf("AVALANCHE") === -1) {
            this._infectPos = [];
            this._xMPos = [];
        }
        this.winMatrix = this.createWinMatrix(data.betClusterWins);
        this.xKnobData = this.calculateXKnobData(data.reels);
        this.xNData = this.calculateXNData(data);
        this.xZData = this.calculateXZData(data);
        this.xMData = this.calculateXMData(data);
        this.xBPMPositions = this.calculateXBPMPositions(data.reels);
        this.winRatio = (data.totalSpinRoundWinnings / data.playedBetValue);
        this.winRatioConfig = this.calculateWinRatioConfig(this.winRatio);

        // #182
        this._infectPos = [...new Set(this._infectPos)];

        for (let win of data.betClusterWins) {
            for (let reel: number = 0; reel < win.reelWinPositions.length; reel++) {
                for (let row: number = 0; row < win.reelWinPositions[reel].length; row++) {
                    if (win.reelWinPositions[reel][row]) {
                        const index: number = this._infectPos.indexOf((DjPsycho.reelsNum * (reel)) + row);
                        if (index > -1) {
                            this._infectPos.splice(index, 1);
                        }
                    }
                }
            }
        }

        if (data.mode == DjPsychoGameMode.NORMAL && (data.nextMode == DjPsychoGameMode.NORMAL || data.nextMode == DjPsychoGameMode.NORMAL_AVALANCHE)) {
            if (data.accumulatedRoundWin === 0) {
                this.noWinCount++;
            } else {
                this.noWinCount = 0;
            }
        }
    }

    private onFeatureName(name: string): void {
        DjPsycho.buyFeatureName = name;
    }

    private createWinMatrix(winCluster: IWinClusterData[]): StateMatrix<DjPsychoSymbolStates> {
        const matrix: StateMatrix<DjPsychoSymbolStates> = new StateMatrix<DjPsychoSymbolStates>();
        matrix.initWithValue(DjPsycho.reelsNum, DjPsycho.rowsNum, DjPsychoSymbolStates.NO_WIN);
        for (let win of winCluster) {
            for (let reel: number = 0; reel < win.reelWinPositions.length; reel++) {
                for (let row: number = 0; row < win.reelWinPositions[reel].length; row++) {
                    if (win.reelWinPositions[reel][row]) {
                        matrix.matrix[reel][row] = DjPsychoSymbolStates.WIN;
                    }
                }
            }
        }
        return matrix;
    }

    private calculateXKnobData(reels: string[][]): IFeaturePosition {
        for (let reel: number = 0; reel < DjPsycho.reelsNum; reel++) {
            for (let row: number = 0; row < DjPsycho.rowsNum; row++) {
                if (reels[reel][row] === "C") {
                    return {
                        reelId: reel,
                        symId: row,
                        symName: "W"
                    };
                }
            }
        }

        return {
            reelId: -1,
            symId: -1,
            symName: ""
        };
    }

    private calculateXNData(data: IDjPsychoGameData): INeighboursData[] {
        const retVal: INeighboursData[] = [],
            reels: string[][] = data.reels,
            evaluatedArea: string[][] = data.evaluatedArea,
            evaluatedArea2: string[][] = data.evaluatedArea2;
        let symName: string;

        reels.forEach((reel: string[], reelId: number) => {
            reel.forEach((name: string, symId: number) => {
                if (name === "xN") {
                    let itemData: INeighboursData = {
                        origin: {
                            reelId: reelId,
                            symId: symId,
                            symName: evaluatedArea[reelId][symId]
                        },
                        infectedPositions: []
                    };

                    if ((reelId - 1) > -1) {
                        if (data.evaluatedArea[reelId - 1][symId] === "xM" && data.evaluatedArea2[reelId - 1][symId] !== "W") {
                            symName = evaluatedArea2[reelId - 1][symId];
                        } else if (data.reels[reelId - 1][symId] === "xM" && data.evaluatedArea2[reelId - 1][symId] === "W") {
                            symName = evaluatedArea[reelId][symId];
                        } else {
                            symName = evaluatedArea[reelId - 1][symId][0] + evaluatedArea[reelId][symId][1];
                        }

                        if (data.reels[reelId - 1][symId] !== "xZ" && this._infectPos.indexOf((DjPsycho.reelsNum * (reelId - 1)) + symId) > -1) {
                            symName = "W";
                        }

                        if (data.reels[reelId - 1][symId] === "xM" && data.evaluatedArea[reelId - 1][symId] !== "xM" && data.evaluatedArea2[reelId - 1][symId] === "W") {
                            this._xMPos.push((DjPsycho.reelsNum * (reelId - 1)) + symId);
                        } else {
                            this._infectPos.push((DjPsycho.reelsNum * (reelId - 1)) + symId);
                        }
                        itemData.infectedPositions.push({
                            reelId: (reelId - 1),
                            symId: symId,
                            symName: symName,
                            angle: 0
                        });
                    }
                    if ((symId - 1) > -1) {
                        if (data.evaluatedArea[reelId][symId - 1] === "xM" && data.evaluatedArea2[reelId][symId - 1] !== "W") {
                            symName = evaluatedArea2[reelId][symId - 1];
                        } else if (data.reels[reelId][symId - 1] === "xM" && data.evaluatedArea2[reelId][symId - 1] === "W") {
                            symName = evaluatedArea[reelId][symId];
                        } else {
                            symName = evaluatedArea[reelId][symId - 1][0] + evaluatedArea[reelId][symId][1];
                        }

                        if (data.reels[reelId][symId - 1] !== "xZ" && this._infectPos.indexOf((DjPsycho.reelsNum * reelId) + (symId - 1)) > -1) {
                            symName = "W";
                        }

                        if (data.reels[reelId][symId - 1] === "xM" && data.evaluatedArea[reelId][symId - 1] !== "xM" && data.evaluatedArea2[reelId][symId - 1] === "W") {
                            this._xMPos.push((DjPsycho.reelsNum * reelId) + (symId - 1));
                        } else {
                            this._infectPos.push((DjPsycho.reelsNum * reelId) + (symId - 1));
                        }
                        itemData.infectedPositions.push({
                            reelId: reelId,
                            symId: (symId - 1),
                            symName: symName,
                            angle: 90
                        });
                    }
                    if ((reelId + 1) < DjPsycho.reelsNum) {
                        if (data.evaluatedArea[reelId + 1][symId] === "xM" && data.evaluatedArea2[reelId + 1][symId] !== "W") {
                            symName = evaluatedArea2[reelId + 1][symId];
                        } else if (data.reels[reelId + 1][symId] === "xM" && data.evaluatedArea2[reelId + 1][symId] === "W") {
                            symName = evaluatedArea[reelId][symId];
                        } else {
                            symName = evaluatedArea[reelId + 1][symId][0] + evaluatedArea[reelId][symId][1];
                        }

                        if (data.reels[reelId + 1][symId] !== "xZ" && this._infectPos.indexOf((DjPsycho.reelsNum * (reelId + 1)) + symId) > -1) {
                            symName = "W";
                        }

                        if (data.reels[reelId + 1][symId] === "xM" && data.evaluatedArea[reelId + 1][symId] !== "xM" && data.evaluatedArea2[reelId + 1][symId] === "W") {
                            this._xMPos.push((DjPsycho.reelsNum * (reelId + 1)) + symId);
                        } else {
                            this._infectPos.push((DjPsycho.reelsNum * (reelId + 1)) + symId);
                        }
                        itemData.infectedPositions.push({
                            reelId: (reelId + 1),
                            symId: symId,
                            symName: symName,
                            angle: 180
                        });
                    }
                    if ((symId + 1) < DjPsycho.rowsNum) {
                        if (data.evaluatedArea[reelId][symId + 1] === "xM" && data.evaluatedArea2[reelId][symId + 1] !== "W") {
                            symName = evaluatedArea2[reelId][symId + 1];
                        } else if (data.reels[reelId][symId + 1] === "xM" && data.evaluatedArea2[reelId][symId + 1] === "W") {
                            symName = evaluatedArea[reelId][symId];
                        } else {
                            symName = evaluatedArea[reelId][symId + 1][0] + evaluatedArea[reelId][symId][1];
                        }

                        if (data.reels[reelId][symId + 1] !== "xZ" && this._infectPos.indexOf((DjPsycho.reelsNum * (reelId)) + (symId + 1)) > -1) {
                            symName = "W";
                        }

                        if (data.reels[reelId][symId + 1] === "xM" && data.evaluatedArea[reelId][symId + 1] !== "xM" && data.evaluatedArea2[reelId][symId + 1] === "W") {
                            this._xMPos.push((DjPsycho.reelsNum * reelId) + (symId + 1));
                        } else {
                            this._infectPos.push((DjPsycho.reelsNum * reelId) + (symId + 1));
                        }
                        itemData.infectedPositions.push({
                            reelId: reelId,
                            symId: (symId + 1),
                            symName: symName,
                            angle: 270
                        });
                    }

                    retVal.push(itemData);
                }
            });
        });

        return retVal;
    }

    private calculateXZData(data: IDjPsychoGameData): INeighboursData[] {
        const retVal: INeighboursData[] = [],
            xMergeTriggered: boolean = (data.listOfMergePositions && data.listOfMergePositions.length > 1),
            reels: string[][] = data.reels,
            evaluatedArea: string[][] = data.evaluatedArea,
            evaluatedArea2: string[][] = data.evaluatedArea2;
        let symName: string;

        reels.forEach((reel: string[], reelId: number) => {
            reel.forEach((name: string, symId: number) => {
                if (name === "xZ") {
                    let itemData: INeighboursData = {
                        origin: {
                            reelId: reelId,
                            symId: symId,
                            symName: evaluatedArea[reelId][symId]
                        },
                        infectedPositions: []
                    }, currentPos: number = -1;

                    // #128 if the origin is already infected more than once from previous feature.
                    if (this._infectPos.filter((val: number) => {
                        return (val === ((DjPsycho.reelsNum * reelId) + symId));
                    }).length > 1) {
                        itemData.origin.symName = "W";
                    }

                    if ((reelId - 1) > -1 && (symId - 1) > -1) {
                        currentPos = (DjPsycho.reelsNum * (reelId - 1)) + (symId - 1);

                        if (data.evaluatedArea[reelId - 1][symId - 1] === "xM" && data.evaluatedArea2[reelId - 1][symId - 1] !== "W") {
                            symName = evaluatedArea2[reelId - 1][symId - 1];
                        } else if (data.reels[reelId - 1][symId - 1] === "xM" && data.evaluatedArea2[reelId - 1][symId - 1] === "W") {
                            // #132
                            if ((this._infectPos.indexOf(currentPos) > -1) && !xMergeTriggered) {
                                symName = "W";
                            } else {
                                symName = evaluatedArea[reelId][symId];
                            }
                        } else {
                            symName = evaluatedArea[reelId - 1][symId - 1][0] + evaluatedArea[reelId][symId][1];
                        }

                        if ((data.reels[reelId - 1][symId - 1] !== "xM") && (this._infectPos.indexOf(currentPos) > -1) && (this._xMPos.indexOf(currentPos) === -1)) {
                            symName = "W";
                        }
                        this._infectPos.push(currentPos);
                        itemData.infectedPositions.push({
                            reelId: (reelId - 1),
                            symId: (symId - 1),
                            symName: symName,
                            angle: 0
                        });
                    }
                    if ((reelId + 1) < DjPsycho.reelsNum && (symId - 1) > -1) {
                        currentPos = (DjPsycho.reelsNum * (reelId + 1)) + (symId - 1);

                        if (data.evaluatedArea[reelId + 1][symId - 1] === "xM" && data.evaluatedArea2[reelId + 1][symId - 1] !== "W") {
                            symName = evaluatedArea2[reelId + 1][symId - 1];
                        } else if (data.reels[reelId + 1][symId - 1] === "xM" && data.evaluatedArea2[reelId + 1][symId - 1] === "W") {
                            // #132
                            if ((this._infectPos.indexOf(currentPos) > -1) && !xMergeTriggered) {
                                symName = "W";
                            } else {
                                symName = evaluatedArea[reelId][symId];
                            }
                        } else {
                            symName = evaluatedArea[reelId + 1][symId - 1][0] + evaluatedArea[reelId][symId][1];
                        }

                        if ((data.reels[reelId + 1][symId - 1] !== "xM") && (this._infectPos.indexOf(currentPos) > -1) && (this._xMPos.indexOf(currentPos) === -1)) {
                            symName = "W";
                        }
                        this._infectPos.push(currentPos);
                        itemData.infectedPositions.push({
                            reelId: (reelId + 1),
                            symId: (symId - 1),
                            symName: symName,
                            angle: 90
                        });
                    }
                    if ((reelId + 1) < DjPsycho.reelsNum && (symId + 1) < DjPsycho.rowsNum) {
                        currentPos = (DjPsycho.reelsNum * (reelId + 1)) + (symId + 1);

                        if (data.evaluatedArea[reelId + 1][symId + 1] === "xM" && data.evaluatedArea2[reelId + 1][symId + 1] !== "W") {
                            symName = evaluatedArea2[reelId + 1][symId + 1];
                        } else if (data.reels[reelId + 1][symId + 1] === "xM" && data.evaluatedArea2[reelId + 1][symId + 1] === "W") {
                            // #132
                            if ((this._infectPos.indexOf(currentPos) > -1) && !xMergeTriggered) {
                                symName = "W";
                            } else {
                                symName = evaluatedArea[reelId][symId];
                            }
                        } else {
                            symName = evaluatedArea[reelId + 1][symId + 1][0] + evaluatedArea[reelId][symId][1];
                        }

                        if ((data.reels[reelId + 1][symId + 1] !== "xM") && (this._infectPos.indexOf(currentPos) > -1) && (this._xMPos.indexOf(currentPos) === -1)) {
                            symName = "W";
                        }
                        this._infectPos.push(currentPos);
                        itemData.infectedPositions.push({
                            reelId: (reelId + 1),
                            symId: (symId + 1),
                            symName: symName,
                            angle: 180
                        });
                    }
                    if ((reelId - 1) > -1 && (symId + 1) < DjPsycho.rowsNum) {
                        currentPos = (DjPsycho.reelsNum * (reelId - 1)) + (symId + 1);

                        if (data.evaluatedArea[reelId - 1][symId + 1] === "xM" && data.evaluatedArea2[reelId - 1][symId + 1] !== "W") {
                            symName = evaluatedArea2[reelId - 1][symId + 1];
                        } else if (data.reels[reelId - 1][symId + 1] === "xM" && data.evaluatedArea2[reelId - 1][symId + 1] === "W") {
                            // #132
                            if ((this._infectPos.indexOf(currentPos) > -1) && !xMergeTriggered) {
                                symName = "W";
                            } else {
                                symName = evaluatedArea[reelId][symId];
                            }
                        } else {
                            symName = evaluatedArea[reelId - 1][symId + 1][0] + evaluatedArea[reelId][symId][1];
                        }

                        if ((data.reels[reelId - 1][symId + 1] !== "xM") && (this._infectPos.indexOf(currentPos) > -1) && (this._xMPos.indexOf(currentPos) === -1)) {
                            symName = "W";
                        }
                        this._infectPos.push(currentPos);
                        itemData.infectedPositions.push({
                            reelId: (reelId - 1),
                            symId: (symId + 1),
                            symName: symName,
                            angle: 270
                        });
                    }

                    retVal.push(itemData);
                }
            });
        });

        return retVal;
    }

    private calculateXMData(data: IDjPsychoGameData): INeighboursData[] {
        const retVal: INeighboursData[] = [],
            evaluatedArea: string[][] = data.evaluatedArea,
            evaluatedArea2: string[][] = data.evaluatedArea2;

        if (!data.listOfMergePositions || !data.listOfMergePositions.length) {
            return retVal;
        }

        let prevSymId: number;
        data.listOfMergePositions.forEach((val: number, idx: number) => {
            const reelId: number = Math.floor(val / DjPsycho.reelsNum),
                symId: number = (val % DjPsycho.rowsNum);
            let angle: number = 0;

            if (idx < 2) {
                let itemData: INeighboursData = {
                    origin: {
                        reelId: reelId,
                        symId: symId,
                        symName: (evaluatedArea2[reelId][symId] === "W" ? evaluatedArea2[reelId][symId] : evaluatedArea[reelId][symId])
                    },
                    infectedPositions: []
                };

                if (idx === 0) {
                    prevSymId = symId;
                }

                retVal.push(itemData);
            } else {
                const source: INeighboursData = retVal[0];

                if (symId > prevSymId) {
                    angle = 270;
                } else if (symId < prevSymId) {
                    angle = 90;
                } else {
                    angle = 180;
                }

                // #147
                this._infectPos.push(val);

                prevSymId = symId;
                source.infectedPositions.push({
                    reelId: reelId,
                    symId: symId,
                    symName: evaluatedArea2[reelId][symId],
                    angle: angle
                });
            }
        });

        return retVal;
    }

    private calculateXBPMPositions(reels: string[][]): number[][] {
        const retVal: number[][] = [];

        reels.forEach((reel: string[], reelId: number) => {
            reel.forEach((name: string, symId: number) => {
                if (name === "xS") {
                    retVal.push([reelId, symId]);
                }
            });
        });

        return retVal;
    }

    private calculateWinRatioConfig(ratio: number): IWinRatioConfig {
        for (let index: number = this.WIN_RATIOS.normalWin.length - 1; index >= 0; index--) {
            if (ratio >= this.WIN_RATIOS.normalWin[index].ratio) {
                return this.WIN_RATIOS.normalWin[index];
            }
        }
        return this.WIN_RATIOS.normalWin[0];
    }

    private setLastConfig(winRatio: number, configs: IBigWinLevel[]): IBigWinLevel[] {
        let lastConfig = configs[configs.length - 1];
        if (lastConfig.from * 1.2 > winRatio && configs.length > 1) {
            configs.pop();
            return this.setLastConfig(winRatio, configs);
        }
        lastConfig.to = winRatio;
        return configs;
    }
}